'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
  badgeVariant?: 'default' | 'warning' | 'danger';
  group?: string;
  subItems?: { label: string; href: string; icon?: string }[];
  activePaths?: string[];
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  userRole?: 'administrator' | 'internal_user' | 'client_user' | 'consultant';
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: 'Squares2X2Icon', group: 'main' },
  { label: 'Clients', href: '/clients', icon: 'BuildingOffice2Icon', group: 'main' },
  { label: 'Inventors', href: '/inventors', icon: 'AcademicCapIcon', group: 'main' },
  {
    label: 'Case Management',
    href: '/case-management',
    icon: 'DocumentTextIcon',
    group: 'main',
    activePaths: ['/new-case', '/cases'],
  },
  // { label: "New Case", href: "/new-case", icon: "PlusCircleIcon", group: "main" },
  {
    label: 'Task Management',
    href: '/task-management',
    icon: 'ClipboardDocumentListIcon',
    group: 'main',
  },
  { label: 'Users', href: '/team', icon: 'UserGroupIcon', group: 'main' },
  {
    label: 'Master',
    href: '#master',
    icon: 'CubeIcon',
    group: 'main',
    subItems: [
      { label: 'Domain', href: '/master/domain', icon: 'GlobeAltIcon' },
      { label: 'Case Type', href: '/master/case-type', icon: 'DocumentTextIcon' },
      { label: 'Country', href: '/master/country', icon: 'GlobeAltIcon' },
      { label: 'Currency', href: '/master/currency', icon: 'CurrencyDollarIcon' },
      { label: 'Case Status', href: '/master/case-status', icon: 'CheckBadgeIcon' },
      { label: 'Record Type', href: '/master/record-type', icon: 'RectangleStackIcon' },
      { label: 'Task Type', href: '/master/task-type', icon: 'ListBulletIcon' },
      { label: 'Industry', href: '/master/industry', icon: 'BuildingLibraryIcon' },
      { label: 'Nationality', href: '/master/nationality', icon: 'FlagIcon' },
    ],
  },
  { label: 'Form Templates', href: '/forms', icon: 'DocumentDuplicateIcon', group: 'ip' },
  { label: 'Renewals-Reminders' /* & Deadlines */, href: '/renewals-reminders', icon: 'ArrowPathIcon', group: 'ip' },
  { label: 'Documents', href: '/documents', icon: 'FolderOpenIcon', group: 'ip' },
  { label: 'Invoices', href: '/invoices', icon: 'BanknotesIcon', group: 'billing' },
  { label: 'Quotations', href: '/quotations', icon: 'DocumentTextIcon', group: 'billing' },
  { label: 'Reports', href: '/reports', icon: 'ChartBarIcon', group: 'admin' },
  { label: 'Notifications', href: '/notifications', icon: 'BellIcon', group: 'admin' },
  { label: 'Settings', href: '/settings', icon: 'Cog6ToothIcon', group: 'admin' },
  { label: 'Audit Trail', href: '/audit', icon: 'ShieldCheckIcon', group: 'admin' },
  { label: 'Access Requests', href: '/access-requests', icon: 'InboxArrowDownIcon', group: 'admin',},
  // { label: "Team & Admin", href: "/team-admin", icon: "UsersIcon", group: "admin" },
  // { label: "Reminder Settings", href: "/reminder-settings", icon: "BellAlertIcon", group: "admin" },
  // { label: 'Settings', href: '/settings', icon: 'Cog6ToothIcon', group: 'admin' },
];

const groupLabels: Record<string, string> = {
  main: 'Core',
  ip: 'IP Management',
  billing: 'Billing',
  admin: 'Administration',
};

export default function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  userRole = 'administrator',
}: SidebarProps) {
  const { user, canModule } = useAuth();
  const pathname = usePathname();
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      setAvatarUrl(user.avatarUrl || localStorage.getItem(`userAvatarUrl_${user.id}`));
    } else {
      setAvatarUrl(null);
    }
    const handleAvatarUpdate = () => {
      if (user?.id) {
        setAvatarUrl(localStorage.getItem(`userAvatarUrl_${user.id}`));
      }
    };
    window.addEventListener('avatar-updated', handleAvatarUpdate);
    return () => window.removeEventListener('avatar-updated', handleAvatarUpdate);
  }, [user?.id]);

  useEffect(() => {
    setExpandedMenus((prev) => {
      const next = { ...prev };
      let changed = false;
      navItems.forEach((item) => {
        if (item.subItems) {
          const isActive = item.subItems.some(
            (sub) => pathname === sub.href || pathname.startsWith(sub.href + '/')
          );
          if (isActive !== prev[item.label]) {
            next[item.label] = isActive;
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [pathname]);

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const visibleItems = navItems.filter((item) => {
    if (userRole === 'client_user') {
      return [
        'Dashboard',
        'Case Management',
        'Task Management',
        'Inventors',
        'Renewals-Reminders',
        'Documents',
        'Form Templates',
        'Invoices',
        'Notifications',
        'Team & Admin',
        'Settings',
      ].includes(item.label);
    }
    if (userRole === 'internal_user') {
      return !['Access Requests', 'Reports', 'Audit Trail'].includes(item.label);
    }
    if (userRole === 'consultant') {
      return !['Master', 'Clients', 'Users', 'Inventors', 'Access Requests', 'Reports', 'Audit Trail'].includes(item.label);
    }

    const modulePermMap: Record<string, string> = {
      'Case Management': 'cases',
      'Task Management': 'tasks',
      Clients: 'clients',
      'Users': 'team',
      Inventors: 'inventory',
      Documents: 'documents',
      Invoices: 'invoices',
      Quotations: 'quotations',
    };

    const moduleKey = modulePermMap[item.label];
    if (moduleKey && !canModule(moduleKey)) {
      return false;
    }

    return true;
  });

  const groups = Array.from(new Set(visibleItems.map((i) => i.group)));

  const getBadgeClass = (variant?: string) => {
    switch (variant) {
      case 'danger':
        return 'bg-red-100 text-red-700';
      case 'warning':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-[100dvh] md:h-screen z-40 flex flex-col bg-white border-r border-border
          transition-all duration-300 ease-in-out
          ${collapsed ? 'w-60 md:w-16' : 'w-60'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div
          className={`flex items-center h-16 border-b border-border px-3 flex-shrink-0 ${collapsed ? 'justify-between md:justify-center md:cursor-pointer md:hover:bg-muted/50 transition-colors' : 'justify-between'}`}
          onClick={collapsed ? onToggle : undefined}
          title={collapsed ? 'Expand sidebar' : undefined}
        >
          <Link href="/dashboard" className="flex items-center gap-2.5 min-w-0 cursor-pointer">
            <AppLogo size={32} />
            <span
              className={`font-semibold text-[15px] text-foreground tracking-tight truncate ${collapsed ? 'md:hidden' : ''}`}
            >
              IPDocket
            </span>
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className={`p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 hidden md:block ${collapsed ? 'md:hidden' : ''}`}
            aria-label="Collapse sidebar"
          >
            <Icon name="ChevronLeftIcon" size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2">
          {groups.map((group) => {
            const groupItems = visibleItems.filter((i) => i.group === group);
            return (
              <div key={group} className="mb-4">
                {!collapsed && (
                  <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground px-2 mb-1.5">
                    {groupLabels[group!]}
                  </p>
                )}
                {collapsed && <div className="h-px bg-border mx-1 mb-2 mt-1" />}
                <ul className="space-y-0.5">
                  {groupItems.map((item) => {
                    const hasSubItems = item.subItems && item.subItems.length > 0;
                    const isExpanded = expandedMenus[item.label];
                    const isActive =
                      pathname === item.href ||
                      (item.href !== '#master' && pathname.startsWith(item.href + '/')) ||
                      (hasSubItems && item.subItems?.some((sub) => pathname === sub.href)) ||
                      item.activePaths?.some(
                        (path) => pathname === path || pathname.startsWith(path + '/')
                      );

                    const itemClasses = `
                      flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm font-medium
                      transition-all duration-150 group relative w-full
                      ${isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                      ${collapsed ? 'md:justify-center' : ''}
                    `;

                    const innerContent = (
                      <>
                        <Icon
                          name={item.icon as any}
                          size={18}
                          variant={isActive ? 'solid' : 'outline'}
                          className="flex-shrink-0"
                        />
                        <span
                          className={`flex-1 truncate text-left ${collapsed ? 'md:hidden' : ''}`}
                        >
                          {item.label}
                        </span>
                        {item.badge !== undefined && (
                          <span
                            className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full tabular-nums ${getBadgeClass(item.badgeVariant)} ${collapsed ? 'md:hidden' : ''}`}
                          >
                            {item.badge}
                          </span>
                        )}
                        {collapsed && item.badge !== undefined && (
                          <span
                            className={`absolute top-1 right-1 w-4 h-4 hidden md:flex items-center justify-center text-[9px] font-700 rounded-full ${getBadgeClass(item.badgeVariant)}`}
                          >
                            {item.badge > 9 ? '9+' : item.badge}
                          </span>
                        )}
                        {hasSubItems && (
                          <Icon
                            name={isExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'}
                            size={14}
                            className={`flex-shrink-0 opacity-70 ${collapsed ? 'md:hidden' : ''}`}
                          />
                        )}
                        {/* Tooltip for collapsed */}
                        {collapsed && tooltipVisible === item.href && (
                          <div className="hidden md:block absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 animate-scale-in pointer-events-none">
                            <div className="bg-foreground text-background text-xs font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap shadow-modal">
                              {item.label}
                              {item.badge !== undefined && (
                                <span className="ml-1.5 opacity-70">({item.badge})</span>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );

                    return (
                      <li key={item.href} className="relative">
                        {hasSubItems ? (
                          <button
                            onClick={() => {
                              if (collapsed) onToggle();
                              toggleMenu(item.label);
                            }}
                            className={itemClasses}
                            onMouseEnter={() => collapsed && setTooltipVisible(item.href)}
                            onMouseLeave={() => setTooltipVisible(null)}
                          >
                            {innerContent}
                          </button>
                        ) : (
                          <Link
                            href={item.href}
                            className={itemClasses}
                            onMouseEnter={() => collapsed && setTooltipVisible(item.href)}
                            onMouseLeave={() => setTooltipVisible(null)}
                          >
                            {innerContent}
                          </Link>
                        )}

                        {hasSubItems && isExpanded && (
                          <ul
                            className={`mt-1 space-y-0.5 ml-8 border-l border-border pl-2 ${collapsed ? 'md:hidden' : ''}`}
                          >
                            {item.subItems!.map((sub) => {
                              const isSubActive = pathname === sub.href;
                              return (
                                <li key={sub.href}>
                                  <Link
                                    href={sub.href}
                                    className={`flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${isSubActive
                                        ? 'font-medium text-primary bg-primary/5'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                      }`}
                                  >
                                    {sub.icon && (
                                      <Icon
                                        name={sub.icon as any}
                                        size={14}
                                        className={
                                          isSubActive ? 'text-primary' : 'text-muted-foreground'
                                        }
                                      />
                                    )}
                                    {sub.label}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* User Profile */}
        {/*
        <div className="flex-shrink-0 border-t border-border p-2">
          <div
            className={`flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer transition-colors duration-150 ${collapsed ? 'md:justify-center' : ''}`}
          >
            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 overflow-hidden border border-border/50">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[11px] font-700 text-primary">
                  {user?.fullName ? user.fullName.substring(0, 2).toUpperCase() : 'U'}
                </span>
              )}
            </div>
            <div className={`flex-1 min-w-0 ${collapsed ? 'md:hidden' : ''}`}>
              <p className="text-[13px] font-600 text-foreground truncate">
                {user?.fullName ?? 'User'}
              </p>
              <p className="text-[11px] text-muted-foreground truncate capitalize">
                {userRole.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>
        */}
      </aside>
    </>
  );
}
